import React, { useState } from 'react';
import {
  IconButton,
  Popover,
  Box,
  Typography,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

/**
 * A reusable help button component that shows explanatory text in a popover.
 *
 * @param {string} text - The help text to display
 * @param {string} title - Optional title for the help popover
 * @param {number} size - Icon size (default: 18)
 * @param {string} color - Icon color (default: 'text.secondary')
 * @param {number} maxWidth - Max width of the popover content (default: 320)
 * @param {string} testId - Optional data-testid for the icon button
 */
function HelpButton({
  text,
  title,
  size = 18,
  color = 'text.secondary',
  maxWidth = 320,
  sx: sxProp,
  testId,
}) {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        data-testid={testId}
        sx={{
          p: 0.5,
          color: color,
          opacity: 0.7,
          '&:hover': {
            opacity: 1,
            bgcolor: 'action.hover',
          },
          ...sxProp,
        }}
      >
        <HelpOutlineIcon sx={{ fontSize: size }} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
              maxWidth: maxWidth,
            },
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          {title && (
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '14px',
                mb: 1,
                color: 'text.primary',
              }}
            >
              {title}
            </Typography>
          )}
          <Typography
            sx={{
              fontSize: '13px',
              color: 'text.secondary',
              lineHeight: 1.6,
            }}
          >
            {text}
          </Typography>
        </Box>
      </Popover>
    </>
  );
}

export default HelpButton;
